import { isConsentLightResponse } from '../consent_agent/consent_agent.types';
/**
 * Converts a consent-light agent payload into {@link Assignment} rows for the given documents and sites.
 * Returns an empty array when `data` is not a valid response; skips unresolved rows and duplicate (site, document) pairs.
 */
export function getAssignmentsFromAgentResponse(data, documents, environmentSites) {
    if (!isConsentLightResponse(data)) {
        return { assignments: [], unrecognizedSites: [], unrecognizedIcfDocuments: [] };
    }
    const assignments = [];
    // Map dedup keys to user-facing display strings to preserve insertion order while deduplicating.
    const unrecognizedSitesByKey = new Map();
    const unrecognizedIcfDocumentsByKey = new Map();
    for (const row of data.assignments) {
        const doc = findConsentFileByDocumentName(documents, row.documentName);
        const site = findEnvironmentSiteForLightAgentRow(environmentSites, row);
        if (!doc || !site) {
            if (!doc) {
                const display = (row.documentName ?? '').trim();
                if (display.length > 0) {
                    unrecognizedIcfDocumentsByKey.set(display, display);
                }
            }
            if (!site) {
                const key = `${row.siteId ?? ''}||${row.siteName ?? ''}`;
                const display = formatUnrecognizedSite(row);
                if (display.length > 0) {
                    unrecognizedSitesByKey.set(key, display);
                }
            }
            continue;
        }
        const alreadyPresent = assignments.find((a) => a.environmentSiteId === site.id && a.consentDocumentId === doc.id);
        if (alreadyPresent !== undefined) {
            continue;
        }
        assignments.push({
            id: `${site.id}-${doc.id}`,
            environmentSiteId: site.id,
            consentDocumentId: doc.id,
        });
    }
    return {
        assignments,
        unrecognizedSites: [...unrecognizedSitesByKey.values()],
        unrecognizedIcfDocuments: [...unrecognizedIcfDocumentsByKey.values()],
    };
}
function formatUnrecognizedSite(row) {
    const name = row.siteName?.trim() ?? '';
    const id = row.siteId?.trim() ?? '';
    if (name.length > 0 && id.length > 0) {
        return `${name} (${id})`;
    }
    return name.length > 0 ? name : id;
}
/** Trims, lowercases, and maps blank strings to `undefined` for case-insensitive comparisons. */
function normalizeComparableName(value) {
    if (value === undefined) {
        return undefined;
    }
    const trimmed = value.trim();
    return trimmed.length === 0 ? undefined : trimmed.toLowerCase();
}
/** Returns the consent file whose `title` matches `documentName` (trimmed, case-insensitive). */
function findConsentFileByDocumentName(documents, documentName) {
    const target = normalizeComparableName(documentName);
    if (target === undefined) {
        return undefined;
    }
    return documents.find((d) => normalizeComparableName(d.title) === target);
}
/**
 * Resolves a consent-light agent row to a site: prefers `siteId` against environment row id or study `siteId`,
 * otherwise matches `siteName` to site `name`, `number`, or `key` (trimmed, case-insensitive).
 */
function findEnvironmentSiteForLightAgentRow(sites, row) {
    const idKey = row.siteId?.trim() ?? '';
    if (idKey.length > 0) {
        const byEnvironmentSiteRowId = sites.find((s) => String(s.id) === idKey);
        if (byEnvironmentSiteRowId) {
            return byEnvironmentSiteRowId;
        }
        const byStudySiteId = sites.find((s) => String(s.siteId) === idKey);
        if (byStudySiteId) {
            return byStudySiteId;
        }
    }
    const nameTarget = normalizeComparableName(row.siteName);
    if (nameTarget === undefined) {
        return undefined;
    }
    return sites.find((s) => {
        const byName = normalizeComparableName(s.name);
        const byNumber = normalizeComparableName(s.number);
        const byKey = normalizeComparableName(s.key);
        return byName === nameTarget || byNumber === nameTarget || byKey === nameTarget;
    });
}
